import { useEffect, useMemo, useState } from "react";

import { useQuery } from "@tanstack/react-query";
import { FileText, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "~/components/ui/select";
import { useCostingPeriods } from "~/hooks/costing";
import { useToast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { costingService } from "~/services/costing";
import { useProfileStore } from "~/store/profileStore";
import type { CostingItemKind, CostingPeriod } from "~/types/costing";
import { formatCurrency } from "~/utils/helpers";

// ---------- Labels ----------

export const ITEM_KIND_LABELS: Record<CostingItemKind, string> = {
  raw_material: "Materia Prima",
  oil_type: "Aceite",
  finished_good: "Producto Terminado",
};

export const MOVEMENT_CLASS_LABELS: Record<string, string> = {
  opening: "Inv. Inicial",
  purchase: "Compra",
  consumption: "Consumo",
  production: "Producción",
  sale: "Venta",
  return: "Devolución",
  adjustment: "Ajuste",
  waste: "Merma",
};

// ---------- Formatting ----------

export const fmtCLP = (value: number | null | undefined): string =>
  value === null || value === undefined ? "—" : `$${formatCurrency(value)}`;

export const fmtQty = (value: number | null | undefined): string =>
  value === null || value === undefined
    ? "—"
    : new Intl.NumberFormat("es-CL", { maximumFractionDigits: 1 }).format(
        value
      );

/** Parse a Chilean/US formatted number ("1.234,56", "1,234.56", "1234.56"). */
export function parseLocaleNumber(raw: string): number | null {
  const s = raw.trim().replace(/\$/g, "").replace(/\s/g, "");
  if (!s) return null;
  let normalized = s;
  if (s.includes(",") && s.includes(".")) {
    normalized =
      s.lastIndexOf(",") > s.lastIndexOf(".")
        ? s.replace(/\./g, "").replace(",", ".") // 1.234,56
        : s.replace(/,/g, ""); // 1,234.56
  } else if (s.includes(",")) {
    normalized = s.replace(",", ".");
  }
  const n = Number(normalized);
  return Number.isFinite(n) ? n : null;
}

// ---------- Oil unit display (liters → kg) ----------

/** Report (`v_costing_movement_report`) quantity columns to convert L→kg for oils. */
export const OIL_REPORT_QTY_FIELDS = [
  "opening_qty",
  "purchases_qty",
  "production_qty",
  "adjustments_in_qty",
  "adjustments_out_qty",
  "availability_qty",
  "consumption_qty",
  "sales_qty",
  "returns_qty",
  "waste_qty",
  "cost_of_sales_qty",
  "closing_qty",
] as const;

/** Oils are stored liters-native in the costing engine (WAC per liter), but finance / Softland
 *  count them in KG. For DISPLAY only, convert a row's quantity fields L→kg (× density) and its
 *  per-unit cost fields ÷ density; $ value fields stay the same (qty_L × cost_L === qty_kg × cost_kg).
 *  The engine/view keep liters. */
export function oilRowToKg<T extends object>(
  row: T,
  density: number | null | undefined,
  qtyFields: readonly string[],
  costFields: readonly string[] = ["wac_clp"]
): T {
  if (!density || density <= 0) return row;
  const out = { ...row } as Record<string, unknown>;
  for (const f of qtyFields) {
    if (out[f] != null) out[f] = Number(out[f]) * density;
  }
  for (const f of costFields) {
    if (out[f] != null) out[f] = Number(out[f]) / density;
  }
  return out as T;
}

/** Ledger-row (`costing_movements`) variant: `quantity` × density, `unit_cost_clp` ÷ density. */
export function oilLedgerRowToKg<T extends object>(
  row: T,
  density: number | null | undefined
): T {
  return oilRowToKg(row, density, ["quantity"], ["unit_cost_clp"]);
}

/** Map of oil_type_id → density_kg_per_liter, for the L→kg display conversion above. */
export function useOilDensities() {
  return useQuery({
    queryKey: ["oil_densities"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("oil_types")
        .select("id, density_kg_per_liter");
      if (error) throw error;
      const map: Record<string, number> = {};
      for (const o of data ?? []) {
        const d = Number(o.density_kg_per_liter);
        if (o.id && d > 0) map[o.id] = d;
      }
      return map;
    },
    staleTime: 60 * 60 * 1000,
  });
}

// ---------- Role gate ----------

/** Costing data entry is limited to superadmin / admin / produccion-admin (PRD §11). */
export function useCanEditCosting(): boolean {
  const role = useProfileStore((s) => s.role);
  return (
    role === "superadmin" || role === "admin" || role === "produccion-admin"
  );
}

export function useIsCostingAdmin(): boolean {
  const role = useProfileStore((s) => s.role);
  return role === "superadmin" || role === "admin";
}

// ---------- Period selection ----------

export function useCosteoPeriod() {
  const { data: periods = [], isLoading } = useCostingPeriods();
  const [period, setPeriod] = useState<string>("");

  // Default to the most recent open period, else the most recent period.
  useEffect(() => {
    if (!period && periods.length > 0) {
      const open = periods.find((p) => p.status === "open");
      setPeriod((open ?? periods[0]).period);
    }
  }, [periods, period]);

  const periodRow: CostingPeriod | undefined = useMemo(
    () => periods.find((p) => p.period === period),
    [periods, period]
  );

  return { periods, period, periodRow, setPeriod, isLoading };
}

interface PeriodSelectorProps {
  periods: CostingPeriod[];
  value: string;
  onChange: (value: string) => void;
}

export function PeriodSelector({
  periods,
  value,
  onChange,
}: PeriodSelectorProps) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="h-9 w-44">
        <SelectValue placeholder="Período" />
      </SelectTrigger>
      <SelectContent>
        {periods.map((p) => (
          <SelectItem key={p.id} value={p.period}>
            {p.period}
            {p.status === "open" ? " (actual)" : " (cerrado)"}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface PeriodRangeSelectorProps {
  periods: CostingPeriod[];
  from: string;
  to: string;
  onFromChange: (value: string) => void;
  onToChange: (value: string) => void;
}

/** Two-month range picker (Desde / Hasta) reusing the costing period list. */
export function PeriodRangeSelector({
  periods,
  from,
  to,
  onFromChange,
  onToChange,
}: PeriodRangeSelectorProps) {
  const renderItems = () =>
    periods.map((p) => (
      <SelectItem key={p.id} value={p.period}>
        {p.period}
        {p.status === "open" ? " (actual)" : " (cerrado)"}
      </SelectItem>
    ));

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm text-muted-foreground">Desde</span>
      <Select value={from} onValueChange={onFromChange}>
        <SelectTrigger className="h-9 w-36">
          <SelectValue placeholder="Mes" />
        </SelectTrigger>
        <SelectContent>{renderItems()}</SelectContent>
      </Select>
      <span className="text-sm text-muted-foreground">Hasta</span>
      <Select value={to} onValueChange={onToChange}>
        <SelectTrigger className="h-9 w-36">
          <SelectValue placeholder="Mes" />
        </SelectTrigger>
        <SelectContent>{renderItems()}</SelectContent>
      </Select>
    </div>
  );
}

// ---------- Item catalogs for manual entry ----------

export interface CostingCatalogProduct {
  id: string;
  sku: string | null;
  short_name: string;
}

export function useCostingProducts() {
  return useQuery({
    queryKey: ["costing_products_catalog"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("products")
        .select("id, sku, short_name")
        .is("deleted_at", null)
        .order("short_name");

      if (error) throw error;
      return data as CostingCatalogProduct[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

// ---------- Document (PDF) viewer — vital for discrepancy analysis ----------

interface PdfViewerDialogProps {
  path: string | null;
  onClose: () => void;
}

export function PdfViewerDialog({ path, onClose }: PdfViewerDialogProps) {
  const { toast } = useToast();
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setUrl(null);
    if (path) {
      costingService
        .getDocumentUrl(path)
        .then((signed) => {
          if (active) setUrl(signed);
        })
        .catch((error) => {
          toast({
            title: "Error",
            description:
              error instanceof Error
                ? error.message
                : "No se pudo abrir el documento",
            variant: "destructive",
          });
          onClose();
        });
    }
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path]);

  return (
    <Dialog open={!!path} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Documento de Recepción
          </DialogTitle>
        </DialogHeader>
        {url ? (
          <iframe
            src={url}
            title="Documento de recepción"
            className="h-[70vh] w-full rounded-md border"
          />
        ) : (
          <div className="flex h-[70vh] items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        )}
        {url && (
          <Button variant="outline" asChild className="self-end">
            <a href={url} target="_blank" rel="noreferrer">
              Abrir en pestaña nueva
            </a>
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
